import {
  computed,
  inject,
  Injectable,
  linkedSignal,
  WritableSignal,
} from '@angular/core';
import { contactDetailTokenizer } from '@data-access/profiles/store/token/contact.token';
import {
  constructDegrees,
  createProfileGroups,
  getProfileCards,
} from '@data-access/profiles/utils/profile-cards.util';
import {
  DegreeData,
  DragDropropData,
  ProfileCard,
  ProfileGroup,
} from '@data-access/profiles/types/contact.type';
import { ContactDetail } from '@data-access/profiles/types';
import {
  DIALOG_TYPE,
  GroupFieldsDialogComponent,
  SingleSelectDialogComponent,
  SingleTextDialogData,
  WcgSingleTextDialogComponent,
} from '@data-access/shared';
import { DialogSize, WcgMenuOption } from '@wcg/frontend-framework';
import { invalidPhoneNumberValidator } from '@shared/validators/phonenumber-validator';
import { Validators } from '@angular/forms';
import {
  GroupFieldsDialogData,
  SingleSelectDialogData,
} from '@data-access/shared/types/dialog.type';
import {
  AddressDialogAbstractView,
  AddressDialogData,
  AddressNew,
  AddressMode,
  AddressType,
  ADDRESS_LABELS,
  ADDRESS_TYPES_ENUM,
  DIALOG_MODES,
} from '@erm/cluster';
import { WcgAddressDialogComponent } from '@data-access/profiles/dialogs';
import { DeleteDialogComponent } from '@shared/dialogs/components';
import { removeMailingAddressDialogData } from '@data-access/profiles/constants/contact.constant';
import { PermissionsService } from '@shared/services/permissions.service';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  PEOPLE_ADMIN_CANEDIT_PERMISSION,
  PEOPLE_ADMIN_CANDELETE_PERMISSION,
} from '@data-access/permissions';
import { IUser } from '@shared/user.interface';
import { filter } from 'rxjs';

@Injectable()
export class ContactFacadeService extends AddressDialogAbstractView {
  private readonly _contactStore = contactDetailTokenizer();
  private readonly _permissions = inject(PermissionsService);
  private readonly _$canEdit = inject(PEOPLE_ADMIN_CANEDIT_PERMISSION);
  private readonly _$canDelete = inject(PEOPLE_ADMIN_CANDELETE_PERMISSION);

  readonly $isInternal = toSignal(this._permissions.isInternal$);
  readonly $canShowEdit = computed(
    () => (this.$isInternal() && this._$canEdit()) || !this.$isInternal(),
  );
  readonly $canShowDelete = computed(
    () => (this.$isInternal() && this._$canDelete()) || !this.$isInternal(),
  );
  readonly $currentUser = toSignal(
    this._permissions.currentUser$.pipe(
      filter((user): user is IUser => !!user),
    ),
  );

  // Address dialog properties
  override addressTitle = 'Add Mailing Address';
  override addressLines = ADDRESS_LABELS;
  override dialogSize = DialogSize.MEDIUM;

  // Signals for raw data
  readonly $data = this._contactStore.$data;
  readonly $error = this._contactStore.$error;
  readonly $isLoading = this._contactStore.$isLoading;
  readonly $degrees = this._contactStore.$degrees;

  // Address dialog abstract properties
  override $countries = this._contactStore.$countries;
  override $states = this._contactStore.$states;

  readonly $profileData = linkedSignal({
    computation: (data: ContactDetail) => this._extractProfileData(data),
    source: this.$data,
  });

  readonly $degreeData: WritableSignal<DegreeData | null> = linkedSignal({
    computation: (data) =>
      data.isPi ? { ...constructDegrees(data.degrees) } : null,
    source: this.$data,
  });

  openPhoneNumberDialog(
    mode: AddressMode,
    options: { field?: string; index?: number; item?: string },
  ): void {
    let dialogData: Partial<SingleTextDialogData<string>> = {
      dialogType: DIALOG_TYPE.SingleText,
      isRequired: true,
      label: 'Phone Number',
      propKey: 'phones',
      validatorFn: invalidPhoneNumberValidator,
    };

    switch (mode) {
      case DIALOG_MODES.CREATE:
        dialogData = {
          ...dialogData,
          data: '',
          errorText: 'Please enter a valid phone number.',
          mode,
          title: 'Add Phone Number',
        };
        break;
      case DIALOG_MODES.EDIT:
        dialogData = {
          ...dialogData,
          data: options.item ?? '',
          errorText: 'Please enter a valid phone number.',
          field: options.field,
          mode,
          title: 'Edit Phone Number',
        };
        break;
      case DIALOG_MODES.DELETE:
        dialogData = {
          ...dialogData,
          data: options.item ?? '',
          description: `Are you sure you want to remove "${options.item}"?`,
          field: options.field,
          mode,
          title: 'Delete Phone Number',
        };
        break;
    }

    this._openSingleTextDialog({
      ...dialogData,
      data: dialogData.data ?? '',
      dialogType: dialogData.dialogType ?? DIALOG_TYPE.SingleText,
      index: options.index,
      isRequired: dialogData.isRequired ?? true,
      label: dialogData.label ?? 'Phone Number',
      mode: dialogData.mode ?? mode,
      title: dialogData.title ?? '',
      validatorFn: dialogData.validatorFn ?? invalidPhoneNumberValidator,
    });
  }

  deleteDegree(options: {
    field?: string;
    index?: number;
    item?: string;
  }): void {
    this._openSingleTextDialog({
      data: options.item ?? '',
      description: `Are you sure you want to remove ${options.item}?`,
      dialogType: DIALOG_TYPE.SingleText,
      field: options.field,
      index: options.index,
      isRequired: true,
      label: 'Degree',
      mode: DIALOG_MODES.DELETE,
      propKey: 'degrees',
      title: 'Remove Degree',
      validatorFn: null,
    });
  }

  deleteMailingAddress(options: {
    field: string;
    index: number;
    item: AddressNew;
  }): void {
    this._contactStore.openAddressDialog({
      addressId: options.item.id,
      mode: DIALOG_TYPE.Delete,
      payload: {
        component: () => DeleteDialogComponent,
        componentData: removeMailingAddressDialogData(options.item, true),
        type: DIALOG_TYPE.Delete,
      },
    });
  }

  openMailingAddressDialog(
    mode: AddressMode,
    options?: { item?: AddressNew },
  ): void {
    this.addressTitle =
      mode === DIALOG_MODES.CREATE
        ? 'Add Physical Address'
        : 'Edit Physical Address';

    const payload: AddressDialogData = {
      facadeService: this,
      mode,
      ...(options?.item && { currentAddress: options.item }),
    };

    this._openAddressDialog(payload, mode, ADDRESS_TYPES_ENUM.location);
  }

  openDegreeDialogSelect(
    mode: AddressMode,
    _: { field?: string; index?: number; item?: string },
  ): void {
    let dialogData: Partial<SingleSelectDialogData<WcgMenuOption>> = {
      isRequired: true,
      label: 'Degree',
      propKey: 'degrees',
      options: this.$degrees(),
      validatorFn: () => Validators.required,
    };

    switch (mode) {
      case DIALOG_MODES.CREATE:
        dialogData = {
          ...dialogData,
          buttonLabel: 'Add',
          data: { id: '', label: '' },
          mode,
          title: 'Add Degree',
        };
        break;
    }

    this._openSingleSelectDialog({
      ...dialogData,
      $canCloseDialog:
        dialogData.$canCloseDialog ?? this._contactStore.$canCloseDialog,
      buttonLabel: dialogData.buttonLabel ?? 'Add',
      callbacks: dialogData.callbacks ?? {
        saveData: (selected: WcgMenuOption) => {
          this._contactStore.addToList({
            propKey: 'degrees',
            value: selected.label,
          });
        },
      },
      data: dialogData.data ?? { id: '', label: '' },
      isRequired: dialogData.isRequired ?? true,
      label: dialogData.label ?? 'Degree',
      mode: dialogData.mode ?? mode,
      options: dialogData.options ?? this.$degrees(),
      title: dialogData.title ?? '',
      validatorFn: dialogData.validatorFn ?? (() => Validators.required),
    });
  }

  moveInList(data: DragDropropData): void {
    this._contactStore.moveInList(data);
  }

  openGroupFieldsDialog(group: ProfileGroup): void {
    const dialogData: GroupFieldsDialogData = {
      group,
      size: DialogSize.SMALL,
      title: `Edit ${group.title ?? ''}`,
    };
    this._openGroupFieldsDialog(dialogData);
  }

  clearContact(): void {
    this._contactStore.clearContact();
  }

  override fetchCountries() {
    this._contactStore.loadCountries();
  }

  override fetchStates(countryId: string) {
    this._contactStore.loadStates(countryId);
  }

  private _extractProfileData(data: ContactDetail): ProfileCard[][] {
    if (!data) {
      return [];
    }

    try {
      const profileCards = this._createProfileCards(data);
      return this._groupCardsIntoRows(profileCards, data.isPi ? 2 : 1);
    } catch (error) {
      console.error('Error extracting profile data:', error);
      return [];
    }
  }

  private _createProfileCards(data: ContactDetail): ProfileCard[] {
    const isInternal = this.$isInternal() ?? false;
    const profileGroups = this._createProfileGroups(data);
    const specialty = this._extractPrimarySpecialty(data.specialty);

    return getProfileCards(
      data,
      specialty,
      profileGroups.phoneGroups,
      profileGroups.addressGroups,
      profileGroups.degreeGroups,
      isInternal,
    );
  }

  private _createProfileGroups(data: ContactDetail): {
    degreeGroups: ProfileGroup[];
    addressGroups: ProfileGroup[];
    phoneGroups: ProfileGroup[];
  } {
    return {
      addressGroups: createProfileGroups(data.mailingAddresses, true),
      degreeGroups: createProfileGroups(data.degrees, false, {
        showDelete: true,
        showDrag: true,
      }),
      phoneGroups: createProfileGroups(data.phones, true),
    };
  }

  private _extractPrimarySpecialty(specialty: string[] | null): string {
    return Array.isArray(specialty) && specialty.length > 0 ? specialty[0] : '';
  }

  private _groupCardsIntoRows(
    cards: ProfileCard[],
    rowSize: number,
  ): ProfileCard[][] {
    if (!cards.length) {
      return [];
    }

    const groupedCards: ProfileCard[][] = [];

    for (let i = 0; i < cards.length; i += rowSize) {
      groupedCards.push(cards.slice(i, i + rowSize));
    }

    return groupedCards;
  }

  private _openSingleTextDialog(
    payload: Omit<SingleTextDialogData<string>, 'size' & { index?: number }>,
  ): void {
    this._contactStore.openSingleTextDialog({
      component: () => WcgSingleTextDialogComponent,
      componentData: {
        size: DialogSize.SMALL,
        ...payload,
      },
      type: DIALOG_TYPE.SingleText,
    });
  }

  private _openAddressDialog(
    payload: AddressDialogData,
    modeType: AddressMode,
    addressType?: AddressType,
  ): void {
    this._contactStore.openAddressDialog({
      addressType: addressType,
      mode: modeType,
      payload: {
        component: () => WcgAddressDialogComponent,
        componentData: payload,
        type: DIALOG_TYPE.Address,
      },
    });
  }

  private _openSingleSelectDialog(
    payload: Omit<SingleSelectDialogData<WcgMenuOption>, 'size'> & {
      index?: number;
    },
  ): void {
    this._contactStore.openSingleTextDialog({
      component: () => SingleSelectDialogComponent,
      componentData: {
        size: DialogSize.SMALL,
        ...payload,
      },
      type: DIALOG_TYPE.SingleSelect,
    });
  }

  private _openGroupFieldsDialog(payload: GroupFieldsDialogData): void {
    this._contactStore.openSingleTextDialog({
      component: () => GroupFieldsDialogComponent,
      componentData: payload,
      type: DIALOG_TYPE.GroupFields,
    });
  }
}
